import { useEffect } from 'react';
import { env } from '@/config/env';

export interface DocumentHeadInput {
  title: string;
  description?: string;
  canonicalPath?: string;
  ogImageUrl?: string | null;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
}

/**
 * Keeps `<head>` in sync with the current route on the client.
 *
 * This is the *second* half of the SEO strategy, not the whole of it. The server
 * already injects correct metadata into the HTML shell for crawlers and link unfurlers
 * (see `apps/api/src/modules/seo/spa-host.ts`); this handles client-side navigation,
 * where no new document is fetched and the injected tags belong to whichever URL the
 * visitor happened to land on first.
 *
 * Both paths resolve their values from the same server-side `SeoService`, so what a
 * crawler reads and what a browser ends up with agree.
 *
 * Implemented directly rather than with a library: it is one effect that sets a title
 * and upserts a handful of meta tags, and a dependency for that would be more code to
 * audit than the code it replaces.
 */
export function useDocumentHead(input: DocumentHeadInput): void {
  const {
    title,
    description,
    canonicalPath,
    ogImageUrl,
    ogType = 'website',
    noIndex = false,
  } = input;

  useEffect(() => {
    if (typeof document === 'undefined') return;

    document.title = title;

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('name', 'twitter:card', ogImageUrl ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('property', 'og:image', ogImageUrl ?? undefined);
    setMeta('name', 'twitter:image', ogImageUrl ?? undefined);
    setMeta(
      'name',
      'robots',
      noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large',
    );

    if (canonicalPath !== undefined) {
      const origin = env.siteUrl || window.location.origin;
      const absolute = `${origin}${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`;
      setCanonical(absolute);
      setMeta('property', 'og:url', absolute);
    }
  }, [title, description, canonicalPath, ogImageUrl, ogType, noIndex]);
}

/**
 * Create or update a meta tag, removing it when the value is absent.
 *
 * Removal matters: navigating from an article with an OG image to a page without one
 * must not leave the previous image behind, or a share of the second page previews with
 * the first page's picture.
 */
function setMeta(attribute: 'name' | 'property', key: string, content: string | undefined): void {
  const selector = `meta[${attribute}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (content === undefined || content === '') {
    existing?.remove();
    return;
  }

  if (existing) {
    existing.setAttribute('content', content);
    return;
  }

  const element = document.createElement('meta');
  element.setAttribute(attribute, key);
  element.setAttribute('content', content);
  document.head.appendChild(element);
}

function setCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}
